import {
  PushNotifications,
  type Token,
  type ActionPerformed,
  type PermissionStatus,
  type PushNotificationSchema,
} from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative, getPlatform } from './platform';
import { deepLinkToRoute } from './deepLinks';
import { supabase } from '@/integrations/supabase/client';
import { wipeLocalDevice } from './wipe';

// Native push registration (P0-PUSH-02).
//
// Web is a NO-OP here: web push uses VAPID and is wired up elsewhere — this module only runs on
// device (guarded by isNative()). The website build is unaffected.
//
// Flow (enablePushNotifications):
//   requestPermissions() -> register() -> on 'registration' capture the APNs/FCM token ->
//   upsert it into public.push_subscriptions { user_id, platform, native_token }.
// On 'pushNotificationActionPerformed' we route the tap to the deep link carried in
// notification.data (the shell wires the router via setNavigate()).
//
// NOTE: push_subscriptions native columns (platform / native_token) are not in the generated
// Supabase types yet — the `as never`/`as any` casts are deliberate, temporary escape hatches.
// TODO: regenerate src/integrations/supabase/types.ts (Supabase MCP generate_typescript_types).

// ── FCM/APNs gate ────────────────────────────────────────────────────────────────────────────────
// Native push requires Firebase to be configured in the Android build (android/app/google-services.json
// + the com.google.gms.google-services Gradle plugin) and an APNs key on iOS. Until those are
// provisioned (HUMAN-GATED — see docs/build-records/SESSION_HANDOFF.md), the Firebase SDK is NOT
// initialized at startup, and calling PushNotifications.register() throws a NATIVE
// "Default FirebaseApp is not initialized" exception on the Capacitor plugin thread that CRASHES the
// whole app — it is NOT catchable from JS (the try/catch around register() cannot save it). So push
// registration is DISABLED here until FCM is wired. Flip this to true in the SAME change that adds
// google-services.json + the Gradle plugin (and the iOS APNs entitlement). With it false the app never
// calls register(), never prompts, and never crashes; web push (VAPID) is unaffected (separate path).
const PUSH_FCM_CONFIGURED: boolean = false;

/** react-router navigate fn, injected by the shell so a notification tap lands on the right screen. */
type NavigateFn = (path: string) => void;
let navigate: NavigateFn | null = null;

/** Wire the app router so a tapped notification can navigate. Call once from the shell. */
export function setNavigate(fn: NavigateFn): void {
  navigate = fn;
}

// Listener handles so the shell can tear everything down on sign-out / unmount.
let handles: PluginListenerHandle[] = [];

async function removeListeners(): Promise<void> {
  const current = handles;
  handles = [];
  await Promise.all(current.map((h) => h.remove().catch(() => {})));
}

/**
 * True when this push is a remote-wipe command (P1 remote-wipe). The server (revoke-device-session →
 * research-notify) sends a silent push with `data.command === 'wipe'`. Honored on BOTH foreground
 * receive and background tap because a silent wipe push may arrive with no user interaction.
 */
function isWipeCommand(data: Record<string, unknown> | undefined): boolean {
  return !!data && data.command === 'wipe';
}

/** Pull a navigable route out of the notification payload (supports raw paths or cbam:// links). */
function routeFromData(data: Record<string, unknown> | undefined): string | null {
  if (!data) return null;
  const raw = (data.deeplink ?? data.deepLink ?? data.link_url ?? data.url ?? data.path) as
    | string
    | undefined;
  if (!raw || typeof raw !== 'string') return null;
  // cbam:// or https app-link → map to an in-app route; otherwise treat as an already-clean path.
  const mapped = deepLinkToRoute(raw);
  if (mapped) return mapped;
  return raw.startsWith('/') ? raw : `/${raw}`;
}

/**
 * Register this device for native push and persist the token (P0-PUSH-02).
 * Web is a no-op. Safe to call multiple times — listeners are reset first and the upsert is idempotent
 * on (user_id, native_token). Returns true if registration was kicked off, false on web / denial / error.
 */
export async function enablePushNotifications(): Promise<boolean> {
  if (!isNative()) return false;

  // HARD GUARD: do not touch the push plugin until FCM is provisioned. register() would crash the
  // app natively (uncatchable) when FirebaseApp is uninitialized. See the PUSH_FCM_CONFIGURED note.
  if (!PUSH_FCM_CONFIGURED) return false;

  try {
    const perm: PermissionStatus = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return false;

    // Fresh listener set each call.
    await removeListeners();

    const registrationHandle = await PushNotifications.addListener(
      'registration',
      async (token: Token) => {
        try {
          const { data: auth } = await supabase.auth.getUser();
          const userId = auth.user?.id;
          if (!userId || !token?.value) return;

          await (supabase.from('push_subscriptions' as never) as any).upsert(
            {
              user_id: userId,
              platform: getPlatform(),
              native_token: token.value,
            },
            { onConflict: 'user_id,native_token' },
          );
        } catch {
          /* token persistence is best-effort; a transient failure must never crash the app */
        }
      },
    );

    const registrationErrorHandle = await PushNotifications.addListener(
      'registrationError',
      () => {
        /* swallow — nothing to persist; web/VAPID handles fallback delivery elsewhere */
      },
    );

    // Foreground delivery: a silent wipe push (data.command === 'wipe') can arrive with no user
    // interaction, so we must honor it here as well as on tap. Only the wipe command is actioned in
    // this listener — ordinary foreground notifications are surfaced/routed via the tap handler.
    const receivedHandle = await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        try {
          if (isWipeCommand(notification?.data as Record<string, unknown>)) {
            // Fire-and-forget; wipeLocalDevice never throws and ends in a hard redirect to login.
            void wipeLocalDevice();
          }
        } catch {
          /* a malformed payload must not break the app */
        }
      },
    );

    const actionHandle = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        try {
          const data = action?.notification?.data as Record<string, unknown>;
          // A wipe takes precedence over any deep-link routing.
          if (isWipeCommand(data)) {
            void wipeLocalDevice();
            return;
          }
          const route = routeFromData(data);
          if (route && navigate) navigate(route);
        } catch {
          /* a malformed payload must not break the app */
        }
      },
    );

    handles = [registrationHandle, registrationErrorHandle, receivedHandle, actionHandle];

    // Triggers the 'registration' (or 'registrationError') event; does not prompt on its own.
    await PushNotifications.register();
    return true;
  } catch {
    return false;
  }
}

/**
 * Stop native push for this device and remove its token row (P0-PUSH-02).
 * Web is a no-op. Tears down listeners and deletes the (user_id, native_token) subscription.
 */
export async function disablePushNotifications(): Promise<void> {
  if (!isNative()) return;

  try {
    // Best-effort: ask the OS to drop the token first so no new pushes arrive.
    await PushNotifications.unregister().catch(() => {});
    await removeListeners();

    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return;

    // We can't always recover the just-unregistered token, so scope the delete to this device's
    // platform rows for the signed-in user. (native_token is the unique key when known.)
    await (supabase.from('push_subscriptions' as never) as any)
      .delete()
      .eq('user_id', userId)
      .eq('platform', getPlatform())
      .not('native_token', 'is', null);
  } catch {
    /* deletion is best-effort; never throw on sign-out */
  }
}
